import { Close as CloseIcon } from '@mui/icons-material'
import { Drawer, useMediaQuery } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { I18n, Translate2 } from 'common'
import { useUiStateStore } from '../../../../../../src/store/uiState/UiStateStore'
import { useSearchStore } from 'src/store/search/SearchStore'
import type { Filters, SortOrder } from 'src/store/models/SearchModels'
import { DEFAULT_SORT_ORDER, useResultsStore } from 'results'
import { defaultTheme } from '../../../../../../src/theme'
import { Button } from '../../../../../../src/ui-components/forms/Button'
import { FilterChipBar } from '../../../../../../src/features/filterResults/components/FilterChipBar'
import { applyFilterSortDrawer } from '../../../../../../src/features/filterResults/applyFilterSortDrawer'
import { removeStagedChip } from '../../../../../../src/features/filterResults/applyStagedFilterChange'
import type { AppliedFilterChipDescriptor } from '../../../../../../src/features/filterResults/types'
import { FilterResultsMenu } from '../FilterResultsMenu'
import { FilterSortDrawerSortFields } from './FilterSortDrawerSortFields'

interface FilterSortDrawerProps {
  visible: boolean
  onClose: () => void
}

export const FilterSortDrawer = observer(
  ({ visible, onClose }: FilterSortDrawerProps): ReactElement => {
    const resultsStore = useResultsStore()
    const searchStore = useSearchStore()
    const { showFilterLinesDrawer } = useUiStateStore()
    const isMobile = useMediaQuery('(max-width:767px)')

    const [stagedFilters, setStagedFilters] = useState<Filters>([])
    const [stagedManufacturers, setStagedManufacturers] = useState<string[]>([])
    const [stagedSort, setStagedSort] = useState<SortOrder>(DEFAULT_SORT_ORDER)

    useEffect(() => {
      if (!visible) {
        return
      }
      setStagedFilters(resultsStore.filters)
      setStagedManufacturers(resultsStore.selectedLinesManufacturers)
      setStagedSort(
        resultsStore.sortOrder.length > 0
          ? resultsStore.sortOrder
          : DEFAULT_SORT_ORDER
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    useEffect(() => {
      if (!visible || showFilterLinesDrawer) {
        return
      }
      setStagedManufacturers(resultsStore.selectedLinesManufacturers)
    }, [
      visible,
      showFilterLinesDrawer,
      resultsStore.selectedLinesManufacturers,
    ])

    const handleClear = (): void => {
      setStagedFilters([])
      setStagedManufacturers([])
      setStagedSort(DEFAULT_SORT_ORDER)
    }

    const handleApply = (): void => {
      applyFilterSortDrawer(
        resultsStore,
        searchStore,
        stagedFilters,
        stagedManufacturers,
        stagedSort
      )
      onClose()
    }

    const handleRemoveChip = (chip: AppliedFilterChipDescriptor): void => {
      const next = removeStagedChip(chip, stagedFilters, stagedManufacturers)
      setStagedFilters(next.filters)
      setStagedManufacturers(next.manufacturers)
    }

    const handleClearChips = (): void => {
      setStagedFilters([])
      setStagedManufacturers([])
    }

    return (
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={visible}
        onClose={onClose}
        PaperProps={{
          sx: isMobile
            ? {
                maxHeight: '92vh',
                borderRadius: '8px 8px 0 0',
                width: '100%',
              }
            : { width: 420, maxWidth: '100vw' },
        }}
      >
        <DrawerContainer>
          <TopSection>
            <Header>
              <Title>
                <I18n name="filterSortResults" />
              </Title>
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label={Translate2('close')}
              >
                <CloseIcon />
              </CloseButton>
            </Header>
            <ChipSection>
              <FilterChipBar
                filtersOverride={stagedFilters}
                onRemoveChip={handleRemoveChip}
                onClearAll={handleClearChips}
                compact
              />
            </ChipSection>
            <TopDivider />
          </TopSection>
          <ScrollContent>
            <FilterResultsMenu
              variant="drawer"
              stagedFilters={stagedFilters}
              onStagedFiltersChange={setStagedFilters}
              stagedManufacturers={stagedManufacturers}
              onStagedManufacturersChange={setStagedManufacturers}
            />
            <FilterSortDrawerSortFields
              sortOrder={stagedSort}
              onChange={setStagedSort}
            />
          </ScrollContent>
          <Footer>
            <Button
              minWidth="47%"
              onClick={handleClear}
              variant="secondary"
              size="medium"
              text="clearFilters"
            />
            <Button
              minWidth="47%"
              variant="primary"
              size="medium"
              onClick={handleApply}
              text="applyFilters"
            />
          </Footer>
        </DrawerContainer>
      </Drawer>
    )
  }
)

const DrawerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 92vh;
  background: ${defaultTheme.colors.white};
`

const TopSection = styled.div`
  flex-shrink: 0;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 0;
  flex-shrink: 0;
`

const Title = styled.h2`
  margin: 0;
  font-family: Oswald, serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 34px;
  text-transform: uppercase;
  color: ${defaultTheme.colors.black};
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${defaultTheme.colors.black};
`

const TopDivider = styled.div`
  border-bottom: 1px solid ${defaultTheme.colors.border};
  width: 100%;
  flex-shrink: 0;
  margin-top: 16px;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 0;
`

const ChipSection = styled.div`
  padding: 0 20px;
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 24px;
  border-top: 1px solid ${defaultTheme.colors.border};
  background: ${defaultTheme.colors.white};
  flex-shrink: 0;
`
